import React from 'react'
import ReactDOM from 'react-dom/client'
import * as Sentry from '@sentry/react'

import './index.css'
import {
  applyCachedTenantConfig,
  applyTenantConfig,
  refreshTenantConfig,
  tenantConfig,
} from './app/config/tenantConfig'
import { getTenantSlug } from './app/services/http/tenant'
import { AccessLoadingScreen } from './view/components/AccessLoadingScreen'

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  release: import.meta.env.VITE_APP_VERSION,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  tracesSampleRate: 0.3,
  beforeSend(event) {
    const status = event.exception?.values?.[0]?.value

    if (
      status?.includes('401') ||
      status?.includes('403') ||
      status?.includes('canceled')
    ) {
      return null
    }

    return event
  },
})

// A marca do parceiro (nome, logo, tema, links) vem da API e alguns módulos a
// leem no import; por isso o App só é importado depois de ter uma config.
// Com config guardada neste navegador, usa ela e atualiza em segundo plano.
// Sem ela, o build multi-tenant (com slug) espera a API montar a config; o
// build sem slug já tem a marca no env e segue direto, buscando em segundo plano.
// No build com slug a tela de carregamento fica até o App terminar de carregar
// (o chunk do App é grande); no sem slug não há tela antes do login.
async function bootstrap() {
  const root = ReactDOM.createRoot(document.getElementById('root')!)
  const hasCache = applyCachedTenantConfig()
  const tenantSlug = getTenantSlug()

  if (tenantSlug) root.render(<AccessLoadingScreen isLoading />)

  if (hasCache) {
    refreshTenantConfig({ background: true })
  } else if (!tenantSlug) {
    applyTenantConfig(tenantConfig())
    refreshTenantConfig({ background: true })
  } else {
    await refreshTenantConfig()
  }

  try {
    const { default: App } = await import('./App')

    const AppWithSentry = Sentry.withErrorBoundary(App, {
      fallback: <p>Erro inesperado.</p>,
    })

    root.render(
      <React.StrictMode>
        <AppWithSentry />
      </React.StrictMode>,
    )
  } catch (error) {
    // Normalmente um bundle antigo que sumiu depois de um deploy.
    Sentry.captureException(error)
    root.render(
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-6 text-center">
        <p>Não foi possível carregar a aplicação.</p>
        <button
          className="rounded-md bg-primary px-4 py-2 text-primary-foreground"
          onClick={() => window.location.reload()}
        >
          Recarregar
        </button>
      </div>,
    )
  }
}

bootstrap()
